import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Toaster } from "sonner";
import { adminApi, api, L, errMsg, exportUrl } from "../lib";
import { Panel, useAdmin, save } from "./parts";
import { HomeTab, LifeTab } from "./HomeLifeTabs";
import ArtTab from "./ArtTab";
import PhotosTab from "./PhotosTab";
import MemoriesTab, { InboxTab } from "./MemoriesTab";
import { MotionTab, ServiceTab } from "./MotionServiceTabs";
import "../louise.css";

function Login({ onIn }) {
  const [f, setF] = useState({ name: "", passcode: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", f);
      localStorage.setItem("lou_admin_token", data.token);
      localStorage.setItem("lou_admin_name", f.name || "Family");
      onIn();
    } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="admin-login-form">
        <p className="font-lou text-3xl">Family dashboard</p>
        <label className="block"><span className="lou-label">Your name</span>
          <input className="lou-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-login-name" /></label>
        <label className="block"><span className="lou-label">Family passcode</span>
          <input type="password" className="lou-input" required value={f.passcode} onChange={(e) => setF({ ...f, passcode: e.target.value })} data-testid="admin-login-passcode" /></label>
        {err && <p className="text-sm text-red-700" role="alert" data-testid="admin-login-error">{err}</p>}
        <button className="lou-btn w-full justify-center" data-testid="admin-login-submit">Sign in</button>
        <Link to={L("/")} className="block text-sm text-lou-soft">← Back to the site</Link>
      </form>
    </div>
  );
}

function Overview({ go }) {
  const [o] = useAdmin("/overview");
  if (!o) return null;
  const cards = [["Memories", o.stories, o.stories_new, "memories"], ["Photographs", o.photos, o.photos_new, "photos"], ["Artworks", o.artworks, 0, "art"],
    ["Print inquiries", o.prints_new, o.prints_new, "inbox"], ["Photo identifications", o.identify_new, o.identify_new, "inbox"], ["Corrections", o.corrections_new, o.corrections_new, "inbox"]];
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {cards.map(([t, n, nw, tab]) => (
          <button key={t} onClick={() => go(tab)} className="bg-white border border-lou-line p-5 text-left hover:border-lou-clay transition-colors" data-testid={`admin-overview-${tab}-${t.split(" ")[0].toLowerCase()}`}>
            <p className="text-xs uppercase tracking-widest text-lou-soft">{t}</p>
            <p className="font-lou text-4xl mt-2">{n}</p>
            {nw > 0 && <p className="text-xs text-lou-clay mt-1">{nw} new</p>}
          </button>
        ))}
      </div>
      {!o.email_configured && <div className="border border-red-700/40 bg-red-50 text-red-800 text-sm p-4 mb-6" data-testid="admin-email-not-configured">Email notifications are not configured — new inquiries and memories are saved here in the dashboard, but no email is sent to greg@eversoul.world.</div>}
      {o.email_configured && o.email_last_failed && <div className="border border-lou-ochre/50 bg-lou-paper text-sm p-4 mb-6" data-testid="admin-email-failing">The most recent email notice did not go through. Submissions are still saved here — check the list below.</div>}
      <Panel title="New-submission notices" testid="admin-email-log">
        <p className="text-sm text-lou-soft mb-3">Each new memory, photograph, inquiry or request triggers an email notice to greg@eversoul.world. Delivery status as reported by the email service:</p>
        {o.email_log.length === 0 && <p className="text-sm">No notices sent yet.</p>}
        <ul className="text-sm divide-y divide-lou-line">{o.email_log.map((l) => (
          <li key={l.id} className="py-2 flex justify-between gap-3"><span>{l.kind}: {l.title}</span>
            <span className={l.status === "sent" ? "text-lou-sage" : "text-red-700"} data-testid="admin-email-status">{l.status.replace("_", " ")}</span></li>))}</ul>
      </Panel>
    </>
  );
}

function ArchiveTab() {
  const [o, load] = useAdmin("/overview");
  const [s, loadS] = useAdmin("/settings");
  return (
    <>
      <Panel title="Export the archive" testid="admin-export">
        <p className="text-sm text-lou-soft mb-4">Download everything — original files, captions, stories, attribution, privacy settings and how records relate — as JSON, CSV and original files in one ZIP.</p>
        <div className="flex flex-wrap gap-3">
          <a className="lou-btn !py-2 text-sm" href={exportUrl(true)} data-testid="admin-export-full">Download full archive (with originals)</a>
          <a className="lou-btn-ghost text-sm" href={exportUrl(false)} data-testid="admin-export-data">Records only (no files)</a>
        </div>
      </Panel>
      <Panel title="Sample content" testid="admin-samples">
        <p className="text-sm text-lou-soft mb-4">{o ? `${o.samples} sample placeholder records exist.` : "Counting sample records…"} They are always labelled SAMPLE. Hide or delete them before sharing the site.</p>
        <div className="flex flex-wrap gap-3">
          <button className="lou-btn-ghost text-sm" data-testid="admin-samples-toggle"
            onClick={async () => { await save(() => adminApi.post("/samples/hide", { show: !s?.show_samples })); loadS(); }}>
            {s?.show_samples ? "Hide samples from visitors" : "Show samples again"}</button>
          <button className="lou-btn !py-2 text-sm !bg-red-800" data-testid="admin-samples-purge"
            onClick={async () => { if (window.confirm("Permanently delete all sample placeholder content?")) { await save(() => adminApi.post("/samples/purge"), "Samples deleted"); load(); loadS(); } }}>
            Delete all samples</button>
        </div>
      </Panel>
      <Panel title="Inviting another family administrator">
        <p className="text-sm text-lou-soft">Share the family passcode privately with a trusted family member and the dashboard link: <code className="bg-lou-ivory px-1">{window.location.origin}{L("/admin")}</code>. Each person signs in with their own name. Individual accounts can be added later.</p>
      </Panel>
    </>
  );
}

const TABS = [["overview", "Overview"], ["home", "Homepage"], ["life", "Her Life"], ["art", "Art"], ["photos", "Photographs"],
  ["memories", "Memories"], ["motion", "In Motion"], ["service", "Arrangements"], ["inbox", "Inbox"], ["archive", "Archive"]];

export default function AdminApp() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("lou_admin_token"));
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    document.title = "Family dashboard — Louise Gatanas";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex,nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
  useEffect(() => {
    if (!authed) return;
    adminApi.get("/me").catch(() => { localStorage.removeItem("lou_admin_token"); setAuthed(false); });
  }, [authed]);
  const out = () => { localStorage.removeItem("lou_admin_token"); setAuthed(false); };
  return (
    <div className="lou-root">
      <Toaster position="bottom-right" />
      {!authed ? <Login onIn={() => setAuthed(true)} /> : (
        <div data-testid="admin-dashboard">
          <header className="bg-lou-ink text-lou-paper">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
              <p className="font-lou text-xl">Louise Gatanas · Family dashboard</p>
              <div className="flex gap-4 text-sm"><span className="opacity-70">Signed in as {localStorage.getItem("lou_admin_name")}</span>
                <Link to={L("/")} className="underline" target="_blank">View site</Link>
                <button onClick={out} className="underline" data-testid="admin-logout">Sign out</button></div>
            </div>
            <nav className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto" aria-label="Dashboard">
              {TABS.map(([k, t]) => <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === k ? "border-lou-ochre text-white" : "border-transparent text-white/60 hover:text-white"}`}>{t}</button>)}
            </nav>
          </header>
          <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
            {tab === "overview" && <Overview go={setTab} />}
            {tab === "home" && <HomeTab />}
            {tab === "life" && <LifeTab />}
            {tab === "art" && <ArtTab />}
            {tab === "photos" && <PhotosTab />}
            {tab === "memories" && <MemoriesTab />}
            {tab === "motion" && <MotionTab />}
            {tab === "service" && <ServiceTab />}
            {tab === "inbox" && <InboxTab />}
            {tab === "archive" && <ArchiveTab />}
          </main>
        </div>
      )}
    </div>
  );
}
